import { useState, useEffect } from "react";
import { Menu, X, User } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import logo from "@/assets/logo-mycing.png";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const height = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / height) * 100;
      setScrollProgress(progress);

      // Detect active section
      if (location.pathname === "/") {
        const sections = ["servicios", "quienes-somos", "proyectos", "contacto"];
        let currentSection = "";
        
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150 && rect.bottom >= 150) {
              currentSection = section;
              break;
            }
          }
        }
        setActiveSection(currentSection);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navLinks = [
    { href: "#servicios", label: "SERVICIOS", sectionId: "servicios" },
    { href: "#quienes-somos", label: "QUIÉNES SOMOS", sectionId: "quienes-somos" },
    { href: "#proyectos", label: "PROYECTOS", sectionId: "proyectos" },
    { href: "/trabaja-con-nosotros", label: "TRABAJA CON NOSOTROS", isRoute: true },
  ];

  const isLinkActive = (link: { href: string; sectionId?: string; isRoute?: boolean }) => {
    if (link.isRoute) {
      return location.pathname === link.href;
    }
    return location.pathname === "/" && activeSection === link.sectionId;
  };

  const handleNavClick = (link: { href: string; label: string; isRoute?: boolean }) => {
    if (link.isRoute) {
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(link.href);
          element?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        element?.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate("/");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Liquid Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 glass-liquid-light backdrop-blur-sm z-[60]">
        <div 
          className="h-full bg-gradient-to-r from-primary via-primary-glow to-primary rounded-full shadow-liquid-md liquid-morph"
          style={{ 
            width: `${scrollProgress}%`,
            boxShadow: '0 0 20px rgba(255, 95, 31, 0.5)'
          }}
        />
      </div>

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 liquid-morph ${
      scrolled 
        ? 'glass-liquid-dark-heavy shadow-liquid-xl backdrop-blur-2xl bg-dark-section/60' 
        : 'glass-liquid-dark-medium shadow-liquid-md backdrop-blur-xl bg-dark-section/50'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <button 
            onClick={handleLogoClick} 
            className="flex items-center cursor-pointer"
          >
            <img 
              src={logo} 
              alt="MYCING Ingeniería Eléctrica" 
              className="h-10 md:h-12 w-auto transition-transform hover:scale-105"
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`font-inter font-medium transition-colors duration-300 relative group text-sm ${
                  isLinkActive(link) 
                    ? 'text-primary' 
                    : 'text-white hover:text-primary'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-primary transition-all duration-300 ${
                  isLinkActive(link) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </button>
            ))}
            <a
              href="https://dash.mycing.cl/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-inter font-semibold text-white px-5 py-2 rounded-full border border-white/30 hover:border-primary hover:text-primary transition-all duration-300 text-sm backdrop-blur-sm bg-white/5"
            >
              <User className="w-4 h-4" />
              ACCESO CLIENTES
            </a>
            <Button 
              variant="default" 
              onClick={() => handleNavClick({ href: "#contacto", label: "CONTACTO" })}
              className={`glass-liquid-medium hover-liquid font-semibold px-6 rounded-full shadow-liquid-md hover:shadow-liquid-xl text-white ${
                activeSection === "contacto" ? "ring-2 ring-primary" : ""
              }`}
            >
              CONTACTO
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white hover:text-primary transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 space-y-4 animate-fade-in glass-liquid-dark-medium rounded-2xl px-4 mb-4 shadow-liquid-lg">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`block w-full text-left font-inter font-medium transition-colors py-2 ${
                  isLinkActive(link) 
                    ? 'text-primary' 
                    : 'text-white hover:text-primary'
                }`}
              >
                {link.label}
              </button>
            ))}
            <a
              href="https://dash.mycing.cl/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 w-full text-white font-inter font-medium py-2 px-4 rounded-full border border-white/30 hover:border-primary hover:text-primary transition-all duration-300 backdrop-blur-sm bg-white/5"
            >
              <User className="w-4 h-4" />
              ACCESO CLIENTES
            </a>
            <Button 
              variant="default" 
              onClick={() => handleNavClick({ href: "#contacto", label: "CONTACTO" })}
              className="w-full bg-primary hover:bg-primary/90"
            >
              CONTACTO
            </Button>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navigation;
